import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid2 from "@mui/material/Unstable_Grid2";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import {
    GridRowModes,
    GridRowParams,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import { SidebarOrderDetails } from "../components/sidebarOrderDetails";
import { SidebarOrderProducts } from "../components/sidebarOrderProducts";
import { SidebarOrderType } from "../components/sidebarOrderType";
import { SidebarOrderAttachments } from "../components/sidebarOrderAttachments";

interface OrdersDetailPanelProps {
    field: string;
    onClose: () => void;
    updateFields: (
        id: string,
        values: { field: string; value: any; errors?: any }[]
    ) => void;
    hiddenColumns: string[];
}

export const OrderDetailsPanel = ({
    field,
    row,
    onClose,
    updateFields,
    hiddenColumns,
}: OrdersDetailPanelProps & GridRowParams<TransportOrderListItemDto>) => {
    const apiRef = useGridApiContext();

    const handleSave = (field: string, value: any, errors?: any) => {
        updateFields(row.id, [{ field, value, errors }]);
    };

    const handleStartEditMode = () => {
        if (apiRef.current?.getRowMode(row.id) === GridRowModes.View) {
            apiRef.current?.startRowEditMode({ id: row.id });
        }
    };

    useEffect(() => {
        handleStartEditMode();
    }, []);

    const showProducts = !hiddenColumns.includes("products");
    const showOrderType = !hiddenColumns.includes("formTitle");
    const colCount = 4 - (showProducts ? 0 : 1) - (showOrderType ? 0 : 1);

    return (
        <Box
            fontSize="small"
            sx={{
                p: 1,
                backgroundColor: "lightBlue.main",
                width: "100%",
                height: "100%",
            }}
        >
            <Grid2 container>
                <Grid2
                    xs={12}
                    md={colCount === 2 ? 6 : 12}
                    lg={12 / colCount}
                    px={1}
                >
                    <SidebarOrderAttachments
                        transportOrder={row}
                        onClose={onClose}
                    />
                </Grid2>
                {showProducts ? (
                    <Grid2
                        xs={12}
                        md={colCount === 2 ? 6 : 12}
                        lg={12 / colCount}
                        px={1}
                    >
                        <SidebarOrderProducts
                            transportOrder={row}
                            onClose={onClose}
                            onProductsAdded={(value, errors) =>
                                handleSave("products", value, errors)
                            }
                            autoFocusFirst={field === "products"}
                        />
                    </Grid2>
                ) : null}
                {showOrderType ? (
                    <Grid2
                        xs={12}
                        md={colCount === 2 ? 6 : 12}
                        lg={12 / colCount}
                        container
                    >
                        <VerticalDividerContainer showMd={colCount === 2} />
                        <HorizontalDividerContainer showMd={colCount !== 2} />
                        <Grid2 xs={true} px={2}>
                            <SidebarOrderType
                                transportOrder={row}
                                onClose={onClose}
                                onSave={(values) =>
                                    updateFields(row.id, values)
                                }
                                autoFocus={field === "orderTypeId"}
                            />
                        </Grid2>
                    </Grid2>
                ) : null}
                <Grid2
                    xs={12}
                    md={colCount === 2 ? 6 : 12}
                    lg={12 / colCount}
                    container
                >
                    <VerticalDividerContainer showMd={colCount === 2} />
                    <HorizontalDividerContainer showMd={colCount !== 2} />
                    <Grid2 xs={true} px={2}>
                        <SidebarOrderDetails
                            autoFocus={field === "transportDetails"}
                            value={row.transportDetails}
                            driverComments={row.driverComments}
                            onClose={onClose}
                            onSaveSuccess={(val) =>
                                handleSave("transportDetails", val)
                            }
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        </Box>
    );
};

interface DiviverContainerProps {
    showMd: boolean;
}

const VerticalDividerContainer = ({ showMd }: DiviverContainerProps) => (
    <Grid2
        xs="auto"
        display={{
            xs: "none",
            md: showMd ? "block" : "none",
            lg: "block",
        }}
    >
        <Divider orientation="vertical" aria-hidden="true" />
    </Grid2>
);

const HorizontalDividerContainer = ({ showMd }: DiviverContainerProps) => (
    <Grid2
        xs={12}
        display={{
            xs: "block",
            md: showMd ? "block" : "none",
            lg: "none",
        }}
        py={2}
    >
        <Divider />
    </Grid2>
);
